import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import { useToken } from '@/common/helper';
import { getVariableValueOptions } from '@/apis/logiblocksAPI';
import type {
  AlertPhaseOption,
  FieldItem,
  FieldOption,
  GroupedFieldOption,
} from '@/apis/logiblocksAPI';
import { nodeToProto, lbNodeToBase64 } from '@/pages/Logiblocks/serialization';
import {
  BoolExpr,
  ComparisonOp,
  DateTimeSetExpr,
  TimeRangeOp,
  VariableRef,
  VariableValue,
} from '@/proto/logiblocks/logiblocks';
import { VariableType } from '@/proto/common';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import { DropdownVariablePicker } from '@/pages/Logiblocks/variables/DropdownVariablePicker';
import {
  findFieldItem,
  getAncestorVariables,
  getAvailableVariablesGroup,
  variableRefName,
} from '@/pages/Logiblocks/variables/variableUtils';
import { getEnumValues } from '@/pages/Logiblocks/variables/variableDefinitions';
import {
  availableOpsForField,
  decodeOp,
  encodeOp,
  expressionOpLabel,
  ExpressionOp,
  getExprLhs,
  getExprOp,
  needsFreeTextRhs,
  setExprLhs,
  setExprOp,
} from './expressionOp';
import {
  comparisonNeedsRhs,
  comparisonIsListRhs,
} from './conditionalNodeUtils';
import { RhsValuePicker } from './RhsVariablePicker';
import { Flex, Select, Tooltip } from 'antd';
import { CustomSelectIcon } from '@/assets/icons/icons';
import { getTriggerFilterContext } from '@/pages/Logiblocks/variables/triggerFilterUtils';

const { Option } = Select;
export interface BoolExprEditorProps {
  nodeId: string;
  expr: BoolExpr;
  onChange: (next: BoolExpr) => void;
  isReadOnly?: boolean;
  whiteInputs?: boolean;
}

/**
 * Editor for a single leaf BoolExpr — value comparison, datetime
 * before/after, or recurring window. Presents LHS variable + a unified
 * operator dropdown (filtered by LHS type) + an op-specific RHS editor.
 */
export const BoolExprEditor: React.FC<BoolExprEditorProps> = ({
  nodeId,
  expr,
  onChange,
  isReadOnly = false,
  whiteInputs = false,
}) => {
  const inputBg = whiteInputs
    ? { backgroundColor: 'var(--surface-container)' }
    : undefined;
  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();
  const lhs = getExprLhs(expr);
  const currentOp = getExprOp(expr);
  const { token } = useToken();
  const [lhsField, setLhsField] = useState<FieldItem | null>(null);
  const fetchingFieldRef = useRef<string | undefined>(undefined);

  // State for dynamic RHS options fetched based on LHS field selection
  const [rhsOptions, setRhsOptions] = useState<
    FieldOption[] | GroupedFieldOption[] | AlertPhaseOption[] | undefined
  >(undefined);

  // Get trigger node and extract filtering context
  const triggerNode = useMemo(() => {
    return nodes.find((n) => {
      const nodeData = n.data;
      return !!(
        nodeData.alert_trigger ||
        nodeData.location_trigger ||
        nodeData.eta_change_trigger ||
        nodeData.status_progress_trigger ||
        nodeData.inbound_event_trigger ||
        nodeData.inbound_trigger ||
        nodeData.manual_execution_trigger ||
        nodeData.schedule_trigger_v2 ||
        nodeData.inbound_call_trigger
      );
    });
  }, [nodes]);

  // Serialize trigger data for deep comparison in useMemo
  const triggerDataKey = useMemo(() => {
    if (!triggerNode) return null;
    const nodeData = triggerNode.data;
    // Create a stable key from the relevant trigger data
    if (nodeData.alert_trigger) {
      return JSON.stringify({
        type: 'alert',
        alertTypes: nodeData.alert_trigger.alert_types,
        selectAllAlertTypes: nodeData.alert_trigger.select_all_alert_types,
      });
    }
    if (nodeData.status_progress_trigger) {
      return JSON.stringify({
        type: 'status',
        trigger: nodeData.status_progress_trigger,
      });
    }
    if (nodeData.location_trigger) {
      return JSON.stringify({
        type: 'location',
        location: nodeData.location_trigger.location,
      });
    }
    if (nodeData.eta_change_trigger) {
      return JSON.stringify({
        type: 'eta',
        changeBy: nodeData.eta_change_trigger.change_by,
      });
    }
    if (nodeData.inbound_event_trigger) {
      return JSON.stringify({ type: 'inboundEvent' });
    }
    if (nodeData.inbound_trigger) {
      return JSON.stringify({
        type: 'inbound',
        eventType: nodeData.inbound_trigger.inbound_event_type,
      });
    }
    if (nodeData.manual_execution_trigger) {
      return JSON.stringify({
        type: 'manual',
        externalTrigger: nodeData.manual_execution_trigger.external_trigger,
      });
    }
    if (nodeData.inbound_call_trigger) {
      return JSON.stringify({ type: 'inboundCall' });
    }
    return null;
  }, [triggerNode]);

  const triggerFilterContext = useMemo(() => {
    if (!triggerNode) return null;
    return getTriggerFilterContext(triggerNode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNode, triggerDataKey]); // triggerDataKey provides deep comparison for trigger data changes

  // Get available variables (same source as the dropdown)
  const ancestorVariables = useMemo(
    () => getAncestorVariables(nodeId, nodes, edges),
    [nodeId, nodes, edges]
  );

  // Includes fields the picker hides from this company, so a saved or copied
  // condition on such a field still loads its type and operators.
  const lookupVariablesGroups = useMemo(() => {
    return getAvailableVariablesGroup(
      ancestorVariables,
      undefined,
      triggerFilterContext,
      true
    );
  }, [ancestorVariables, triggerFilterContext]);

  // Helper: Check if field type needs RHS options from API
  const needsRhsOptions = useCallback((fieldType: number): boolean => {
    const type = fieldType as VariableType;
    return (
      type === VariableType.STRING ||
      type === VariableType.ALERT_TYPE ||
      type === VariableType.TRANSPORT_PROVIDER
    );
  }, []);

  // Helper: Fetch RHS options for a variable, narrowed by the node that publishes it
  const fetchRhsOptions = useCallback(
    (ref: VariableRef) => {
      if (!token) return;

      const variableName = variableRefName(ref);

      // The LHS variable is always published by an ancestor node, so its node
      // is expected in the graph; a miss means the graph is inconsistent.
      const producingNode = nodes.find((n) => n.id === ref.node_id);
      if (!producingNode) {
        console.error(`No node found publishing variable "${variableName}"`);
        return;
      }

      // Prevent duplicate fetches. Two variables can share a name on one node,
      // one a node output and one a user variable, so the source is part of
      // the key.
      const fetchKey = `${ref.node_id}.${variableName}.${ref.source}`;
      if (fetchingFieldRef.current === fetchKey) return;
      fetchingFieldRef.current = fetchKey;

      const serialized_data = lbNodeToBase64(nodeToProto(producingNode));

      getVariableValueOptions(token, {
        variable_name: ref.variable_name,
        derived_variable_name: ref.derived_variable_name,
        source: ref.source,
        serialized_data,
      })
        .then((result) => {
          // If the backend knows this field is a more specific type than STRING
          // (e.g. DATE_TIME or DATE_TIME_RANGE), upgrade lhsField so the correct
          // operators are offered without requiring the user to re-open the AI
          // call action node after picking the variable.
          if (result.type !== VariableType.STRING) {
            setLhsField((prev) =>
              prev ? { ...prev, type: result.type } : prev
            );
          }
          if (result.options) {
            setRhsOptions(result.options);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch RHS options:', error);
          setRhsOptions(undefined);
        });
    },
    [nodes, token]
  );

  // Fill the RHS dropdown for a newly selected LHS field.
  // An enum type's values come from the variable definitions file, with no request.
  const loadRhsOptions = useCallback(
    (field: FieldItem, nodeId: string) => {
      const enumValues = getEnumValues(field.type as VariableType);
      if (enumValues) {
        setRhsOptions(enumValues);
        return;
      }
      if (needsRhsOptions(field.type)) {
        // The names come off the matched field rather than the stored ref, which
        // may still hold both of them joined by a dot.
        fetchRhsOptions({
          node_id: nodeId,
          variable_name: field.field_name,
          derived_variable_name: field.derived_field_name ?? '',
          source: field.source,
        });
        return;
      }
      setRhsOptions(undefined);
    },
    [needsRhsOptions, fetchRhsOptions]
  );

  // Initialize lhsField ONLY on mount/reload when saved expression exists
  // This runs once when component mounts with an existing lhs value
  // User selection is handled separately in handleLhsChange
  useEffect(() => {
    // Only initialize if lhs exists but lhsField is not yet set (initial load)
    if (!lhs?.variable_name || lhsField !== null) {
      return;
    }

    const foundField = findFieldItem(lhs, lookupVariablesGroups);
    if (foundField) {
      setLhsField(foundField);
      loadRhsOptions(foundField, lhs.node_id);
    }
  }, [
    lhs,
    lhs?.variable_name,
    lhs?.derived_variable_name,
    lhs?.node_id,
    lhsField,
    lookupVariablesGroups,
    loadRhsOptions,
  ]);

  // Derive LHS type from lhsField
  const lhsType = useMemo((): VariableType | undefined => {
    if (lhsField) {
      return lhsField.type as VariableType;
    }
    return undefined;
  }, [lhsField]);

  // Get available operators based on lhsField
  const ops = lhsField ? availableOpsForField(lhsField) : [];

  // Replace the LHS variable. If the new variable's type makes the current
  // op invalid (e.g. switching from a STRING to a DATE_TIME variable while
  // EQUALS is selected), snap to the first op available for the new type so
  // the dropdown's value always matches a real option.
  const handleLhsChange = (
    newLhs: (VariableRef & { item: FieldItem }) | null
  ) => {
    if (!newLhs) {
      onChange({
        comparison_expr: {
          variable: undefined,
          operator: -1 as ComparisonOp,
          rhs: undefined,
          rhs2: undefined,
        },
      });
      setLhsField(null);
      setRhsOptions(undefined);
      return;
    }

    const LHS: VariableRef = {
      node_id: newLhs.node_id,
      variable_name: newLhs.variable_name,
      derived_variable_name: newLhs.derived_variable_name,
      source: newLhs.source,
    };
    let updated = setExprLhs(expr, LHS);
    const newField = newLhs.item;
    setLhsField(newField);

    const newOps = availableOpsForField(newField);
    const stillAvailable =
      currentOp &&
      newOps.some((o) => o.kind === currentOp.kind && o.op === currentOp.op);

    if (!stillAvailable && newOps.length > 0) {
      // setExprOp already clears rhs/rhs2
      updated = setExprOp(updated, newOps[0]);
    } else if (updated.comparison_expr) {
      // Op stays valid but RHS is stale for the new LHS field — clear it
      updated = {
        comparison_expr: {
          ...updated.comparison_expr,
          rhs: undefined,
          rhs2: undefined,
        },
      };
    }
    onChange(updated);

    loadRhsOptions(newField, LHS.node_id);
  };

  // Morph the expression to a new operator. setExprOp resets RHS state to
  // avoid carrying stale fields when the underlying proto branch flips.
  const handleOpChange = (encoded: string) => {
    const op = decodeOp(encoded);
    onChange(setExprOp(expr, op));
  };

  // Write a typed literal into the comparison's rhs. BoolExpr is a proto
  // oneof, so a leaf that was a date-time set / recurring window becomes a
  // fresh comparison_expr on its variable.
  const handleLiteralChange = (value: VariableValue | null) => {
    const variable = getExprLhs(expr);
    if (!variable && !expr.comparison_expr) return;

    const baseOperator =
      expr.comparison_expr && (expr.comparison_expr.operator as number) >= 0
        ? expr.comparison_expr.operator
        : ComparisonOp.COMPARISON_EQUALS;

    onChange({
      comparison_expr: {
        variable,
        operator: baseOperator,
        rhs: value ? { literal: value } : undefined,
        rhs2: undefined,
      },
    });
  };

  // Write a variable reference into the comparison's rhs.
  const handleRhsVariableChange = (ref: VariableRef | null) => {
    if (!expr.comparison_expr) return;
    onChange({
      comparison_expr: {
        ...expr.comparison_expr,
        rhs: ref ? { variable: ref } : undefined,
      },
    });
  };

  // Write a list of literals into the comparison's rhs.list (CONTAINS / NOT_CONTAINS / IN_LIST).
  const handleListChange = (values: VariableValue[]) => {
    if (!expr.comparison_expr) return;
    onChange({
      comparison_expr: {
        ...expr.comparison_expr,
        rhs: values.length > 0 ? { list: { items: values } } : undefined,
        rhs2: undefined,
      },
    });
  };

  return (
    <>
      <Flex className='w-full' vertical gap={8}>
        {/* Row 1: LHS */}
        <DropdownVariablePicker
          nodeId={nodeId}
          value={lhs}
          onChange={handleLhsChange}
          placeholder='Select...'
          disabled={isReadOnly}
          whiteBackground={whiteInputs}
        />

        {/* Row 2: Operator */}
        <Tooltip
          title={
            !lhs ? 'Start by selecting an option from the first dropdown.' : ''
          }
          overlayClassName='font-10'
        >
          <Select
            placeholder='Select...'
            onChange={(value) => handleOpChange(value)}
            value={currentOp ? encodeOp(currentOp) : undefined}
            disabled={isReadOnly || !lhs}
            className='lb-select'
            suffixIcon={<CustomSelectIcon />}
            style={{ width: '50%', ...inputBg }}
          >
            {ops.map((op: ExpressionOp) => (
              <Option key={encodeOp(op)} value={encodeOp(op)}>
                {expressionOpLabel(op)}
              </Option>
            ))}
          </Select>
        </Tooltip>

        {/* Row 3: RHS — shown when comparisonExpr needs a value OR
            recurringWindowExpr/dateTimeSetExpr is active */}
        {((expr.comparison_expr &&
          comparisonNeedsRhs(expr.comparison_expr.operator)) ||
          !!expr.recurring_window_expr ||
          !!expr.date_time_set_expr) && (
          <>
            {/* Show variable picker if RHS is a variable */}
            {expr.comparison_expr?.rhs?.variable ? (
              <DropdownVariablePicker
                nodeId={nodeId}
                value={expr.comparison_expr.rhs.variable}
                onChange={handleRhsVariableChange}
                placeholder='Select...'
                disabled={isReadOnly || !lhs}
                whiteBackground={whiteInputs}
              />
            ) : (
              <Tooltip
                title={
                  !lhs
                    ? 'Start by selecting an option from the first dropdown.'
                    : ''
                }
                overlayClassName='font-10'
              >
                <RhsValuePicker
                  lhsType={lhsType}
                  options={rhsOptions ?? []}
                  grouped={lhsField?.group}
                  value={expr.comparison_expr?.rhs?.literal}
                  onChange={handleLiteralChange}
                  disabled={isReadOnly || !lhs}
                  whiteBackground={whiteInputs}
                  placeholder='Select...'
                  forceTextInput={needsFreeTextRhs(lhsField, currentOp)}
                  isMultiValue={
                    !!expr.comparison_expr &&
                    comparisonIsListRhs(expr.comparison_expr.operator)
                  }
                  listValue={expr.comparison_expr?.rhs?.list?.items}
                  onListChange={handleListChange}
                  isTeamsField={
                    (lhsField?.derived_field_name || lhsField?.field_name) ===
                    'teams'
                  }
                  recurringWindowExpr={expr.recurring_window_expr}
                  dateTimeSetExpr={
                    expr.date_time_set_expr ??
                    DateTimeSetExpr.create({
                      variable: expr.comparison_expr?.variable,
                      operator: TimeRangeOp.WITHIN,
                      iana_timezone: '',
                    })
                  }
                  onDateTimeSetExprChange={(next) => {
                    onChange({ date_time_set_expr: next });
                  }}
                />
              </Tooltip>
            )}
          </>
        )}
      </Flex>
    </>
  );
};
